import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from './firebase'
import { DEFAULT_FEATURES, withDefaults, type FeatureKey, type Features } from '@shared/features'

/**
 * Vilka funktioner som är påslagna.
 *
 * Läses direkt ur `settings/features`, som är öppen för alla – även utloggade,
 * eftersom beställningssidan och inloggningssidan båda behöver veta.
 *
 * Lyssnar i stället för att hämta en gång. Slår en superadmin på en funktion
 * ska den som redan har appen öppen se den utan att ladda om; annars ringer
 * någon och säger att det inte fungerar, och det stämmer just då.
 *
 * Medan svaret är på väg gäller allt-av. Det är rätt håll att gissa åt: en
 * knapp som dyker upp en halv sekund sent är en småsak, en beställningssida
 * som blinkar förbi innan den försvinner är en supportfråga.
 */
export function useFeatures(): { features: Features; loading: boolean } {
  const [features, setFeatures] = useState<Features>(DEFAULT_FEATURES)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'features'),
      (snapshot) => {
        setFeatures(withDefaults(snapshot.data() as Partial<Features> | undefined))
        setLoading(false)
      },
      () => {
        // Offline eller inget dokument. Allt-av är redan utgångsläget.
        setLoading(false)
      },
    )
    return unsubscribe
  }, [])

  return { features, loading }
}

/** Bekvämlighet för de många ställen som bara bryr sig om en enda flagga. */
export function useFeature(key: FeatureKey): boolean {
  return useFeatures().features[key]
}
